import { isProxy, toRaw } from 'vue'

/**
 * Tracks changes and new instances of a list of T models
 */
export function useDirtyTracker<T extends WeakKey>() {
  let originals = new WeakMap<T, T>()
  const created = new Set<T>()

  function resetTracking() {
    originals = new WeakMap<T, T>()
  }

  /**
   * If given a proxy, unwraps it.
   * Otherwise original object is returned
   * @param T obj
   * @returns T
   */
  function unwrapProxy<T>(obj: T): T {
    if (isProxy(obj)) return toRaw(obj)

    return obj
  }

  /**
   * Includes a model into the tracking map
   * @param model
   */
  function track(model: T) {
    if (!originals.has(model)) {
      const copy = { ...(model as object) }
      originals.set(model, copy as T)
    }
  }

  /**
   * Removes a model from the tracking map
   */
  function untrack(model: T) {
    if (originals.has(model)) originals.delete(model)
  }

  /**
   * Inserts a new model into the tracker. New models aren't treated as dirty.
   * @param T model
   */
  function markAsNew(model: T) {
    created.add(unwrapProxy(model))
  }

  /**
   * Inserts a new model into the tracker. New models aren't treated as dirty.
   * @param T model
   */
  function unmarkAsNew(model: T) {
    created.delete(unwrapProxy(model))
  }

  /**
   * Checks for alterations on a given model for up to 2 layers of deepness.
   * Handle proxies, primitives, arrays of primitives and objects
   * @param T model
   * @returns boolean
   */
  function isDirty(model: T): boolean {
    if (!originals.has(model)) return false

    const original = originals.get(model)

    if (original === undefined) return false

    const keys = Object.keys(model) as (keyof T)[]

    for (const key of keys) {
      const currentValue = unwrapProxy(model[key])
      const originalValue = unwrapProxy(original[key])

      // Add a second level of deepness check.
      // Opted for this as a recursive check could get expensive if used without proper care
      const isNested = typeof currentValue === 'object' && currentValue !== null && typeof originalValue === 'object' && originalValue !== null
      const isArray = Array.isArray(currentValue) && Array.isArray(originalValue)

      if (isArray) {
        if (currentValue.length !== originalValue.length) {
          return true
        }
        for (let i = 0; i < currentValue.length; i++) {
          if (currentValue[i] !== originalValue[i]) {
            return true
          }
        }
      } else if (isNested) {
        const nestedKeys = Object.keys(currentValue) as (keyof T[keyof T])[]

        for (const nestedKey of nestedKeys) {
          if (currentValue[nestedKey] !== originalValue[nestedKey]) {
            return true
          }
        }
      } else if (currentValue !== originalValue) {
        return true
      }
    }

    return false
  }

  /**
   * Return wether an object is newly created or not
   * @param T model
   * @returns boolean
   */
  function isNew(model: T): boolean {
    return created.has(unwrapProxy(model))
  }

  return { track, untrack, resetTracking, unmarkAsNew, markAsNew, isDirty, isNew }
}
